/**
 * createWhere — SQL WHERE 절 빌더
 *
 * 메서드 체이닝으로 조건을 추가하고, build()로 SQL과 바인딩 값을 반환한다.
 * 값이 undefined / null / 빈 문자열이면 자동으로 스킵한다.
 *
 * @example
 * const w = createWhere();
 *
 * w.equal("U.status", data.status);          // undefined면 스킵
 * w.like("U.name", data.keyword);            // → U.name LIKE '%keyword%'
 * w.likeAny(["U.name", "U.email"], data.keyword); // 검색 유형이 "전체"일 때 → (U.name LIKE ? OR U.email LIKE ?)
 * w.between("U.createdAt", data.startDate, data.endDate);
 * w.in("U.role", ["admin", "manager"]);
 * w.raw("U.deletedAt IS NULL");
 *
 * const { whereSql, whereValues } = w.build();
 * // whereSql  → " WHERE U.name LIKE ? AND U.createdAt BETWEEN ? AND ? AND U.deletedAt IS NULL"
 * // whereValues → ["%keyword%", startDate, endDate]
 */

interface WhereClause {
    sql: string;
    vals: unknown[];
}

export function createWhere() {
    const clauses: WhereClause[] = [];

    const isEmpty = (v: unknown) =>
        v === undefined || v === null || v === "";

    return {
        /**
         * col = ?
         * val이 undefined / null / ""이면 스킵
         */
        equal(col: string, val: unknown) {
            if (!isEmpty(val)) {
                clauses.push({ sql: `${col} = ?`, vals: [val] });
            }
            return this;
        },

        /**
         * col LIKE '%val%'
         * val이 falsy이면 스킵
         */
        like(col: string, val: unknown) {
            if (val && typeof val === "string" && val.trim() !== "") {
                clauses.push({ sql: `${col} LIKE ?`, vals: [`%${val.trim()}%`] });
            }
            return this;
        },

        /**
         * (col1 LIKE ? OR col2 LIKE ? OR ...)
         * 키워드 검색 유형이 "전체"일 때처럼, 여러 컬럼에 동시에 LIKE 검색할 때 사용.
         * val이 falsy이거나 cols가 빈 배열이면 스킵.
         * @example w.likeAny(["U.name", "U.email"], data.keyword)
         */
        likeAny(cols: string[], val: unknown) {
            if (cols.length > 0 && val && typeof val === "string" && val.trim() !== "") {
                const kw = `%${val.trim()}%`;
                clauses.push({
                    sql: `(${cols.map((c) => `${c} LIKE ?`).join(" OR ")})`,
                    vals: cols.map(() => kw),
                });
            }
            return this;
        },

        /**
         * col BETWEEN ? AND ?
         * start 또는 end가 falsy이면 스킵
         */
        between(col: string, start: unknown, end: unknown) {
            if (start && end) {
                clauses.push({ sql: `${col} BETWEEN ? AND ?`, vals: [start, end] });
            }
            return this;
        },

        /**
         * col IN (?, ?, ...)
         * vals가 빈 배열이면 스킵
         */
        in(col: string, vals: unknown[]) {
            if (vals.length > 0) {
                const placeholders = vals.map(() => "?").join(", ");
                clauses.push({ sql: `${col} IN (${placeholders})`, vals });
            }
            return this;
        },

        /**
         * col IS NULL
         */
        isNull(col: string) {
            clauses.push({ sql: `${col} IS NULL`, vals: [] });
            return this;
        },

        /**
         * col IS NOT NULL
         */
        isNotNull(col: string) {
            clauses.push({ sql: `${col} IS NOT NULL`, vals: [] });
            return this;
        },

        /**
         * 직접 SQL 조각과 바인딩 값을 추가
         * @example w.raw("(U.role = ? OR U.role = ?)", ["admin", "manager"])
         */
        raw(sql: string, vals: unknown[] = []) {
            clauses.push({ sql, vals });
            return this;
        },

        /** WHERE 절 SQL과 바인딩 값 배열을 반환 */
        build(): { whereSql: string; whereValues: unknown[] } {
            if (clauses.length === 0) {
                return { whereSql: "", whereValues: [] };
            }
            const whereSql = " WHERE " + clauses.map((c) => c.sql).join(" AND ");
            const whereValues = clauses.flatMap((c) => c.vals);
            return { whereSql, whereValues };
        },
    };
}
